// ISMÉTLŐDŐ MEGBESZÉLÉS — a felület tiszta segédei: az űrlap-mezők ↔ szabály, az előnézet és a sorozat emberi
// összefoglalója. A kibontás maga a KÖZÖS `@atlas/shared` `expandRecurrence` (ugyanaz, amit a BFF ír) — így az
// előnézet pontosan azt ígéri, ami létrejön.
import {
  expandRecurrence,
  type ExpandResult,
  type RecurrenceFrequency,
  type RecurrenceRule,
} from '@atlas/shared';
import { type TFunction } from 'i18next';

import { DEFAULT_TIME_ZONE, formatDateTime, localeFor } from '@/lib/format-datetime';

export const RECURRENCE_FREQUENCIES: readonly RecurrenceFrequency[] = [
  'weekly',
  'biweekly',
  'monthly',
  'workdays',
];

/** Az űrlap ismétlődés-mezői (a `none` = egyszeri megbeszélés). */
export interface RecurrenceFormValues {
  recurrenceFrequency: 'none' | RecurrenceFrequency;
  /** ISO hét-napok szövegként ('1' = hétfő … '7' = vasárnap) — a szegmentált mező értéke. */
  recurrenceWeekdays: string[];
  recurrenceEnd: 'count' | 'until';
  recurrenceCount: string;
  /** `YYYY-MM-DD` */
  recurrenceUntil: string;
}

export const RECURRENCE_DEFAULTS: RecurrenceFormValues = {
  recurrenceFrequency: 'none',
  recurrenceWeekdays: [],
  recurrenceEnd: 'count',
  recurrenceCount: '4',
  recurrenceUntil: '',
};

/** Hét-napos-e a szabály (heti / kétheti). */
export function usesWeekdays(frequency: RecurrenceFormValues['recurrenceFrequency']): boolean {
  return frequency === 'weekly' || frequency === 'biweekly';
}

/** Az űrlap-mezők → szabály (`none` → null). A darabszám csak pozitív egész lehet, különben hiányzó. */
export function recurrenceRuleOf(v: RecurrenceFormValues): RecurrenceRule | null {
  if (v.recurrenceFrequency === 'none') return null;
  const count = Number(v.recurrenceCount);
  return {
    frequency: v.recurrenceFrequency,
    weekdays: usesWeekdays(v.recurrenceFrequency) ? v.recurrenceWeekdays.map(Number) : [],
    untilDate: v.recurrenceEnd === 'until' && v.recurrenceUntil !== '' ? v.recurrenceUntil : null,
    occurrenceCount:
      v.recurrenceEnd === 'count' && Number.isInteger(count) && count > 0 ? count : null,
  };
}

/** Az ISO hét-nap ('1'…'7') egy helyi `YYYY-MM-DDTHH:mm` (vagy `YYYY-MM-DD`) kezdésből. */
export function isoWeekdayOfLocal(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(local);
  if (!m) return null;
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return String(((day + 6) % 7) + 1);
}

/** Az előnézet / a kliens-oldali elő-validálás: a kibontás az ISO kezdés/vég és a szabály alapján. */
export function previewRecurrence(
  startsAtIso: string,
  endsAtIso: string,
  rule: RecurrenceRule,
): ExpandResult {
  return expandRecurrence(startsAtIso, endsAtIso, rule, DEFAULT_TIME_ZONE);
}

/** A hét-napok rövid nevei a nyelven (ISO sorrendben, hétfőtől) — `Intl`-ből, nem kézzel írt szótárból. */
export function weekdayNames(
  language: string,
  style: 'short' | 'long' = 'short',
): { value: string; label: string }[] {
  // egy ismert hétfő (UTC) — a hét napjai innen, sorban
  return Array.from({ length: 7 }, (_, i) => ({
    value: String(i + 1),
    label: new Intl.DateTimeFormat(localeFor(language), {
      weekday: style,
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2024, 0, 1 + i))),
  }));
}

/** A lista-sor sorozat-adata (a contract `MeetingSeriesInfoSchema` alakja). */
export interface SeriesInfo {
  frequency: string;
  weekdays: number[];
  untilDate: string | null;
  occurrenceCount: number | null;
  liveCount: number | null;
  lastDate: string | null;
}

/**
 * A sorozat emberi összefoglalója: „Hetente: K, Cs · 11 alkalom · 2027. jan. 12-ig". A szám és a dátum az ÉLŐ
 * alkalmakból jön (törlés után is a valóság); ha az élő adat nem olvasható, a létrehozáskori szabály
 * („12 alkalom" / „2027. jan. 19-ig").
 */
export function describeSeries(series: SeriesInfo, t: TFunction, language: string): string {
  const parts: string[] = [
    t(`meetings.recurrence.frequency.${series.frequency}`, { defaultValue: series.frequency }),
  ];
  if (series.weekdays.length > 0) {
    const names = weekdayNames(language);
    const days = series.weekdays
      .slice()
      .sort((a, b) => a - b)
      .map((d) => names[d - 1]?.label ?? String(d))
      .join(', ');
    parts[0] = `${parts[0]}: ${days}`;
  }
  const untilLabel = (day: string) =>
    t('meetings.recurrence.until', {
      date: formatDateTime(`${day}T12:00:00.000Z`, language, DEFAULT_TIME_ZONE, {
        dateStyle: 'medium',
      }),
    });
  if (series.liveCount !== null) {
    parts.push(t('meetings.recurrence.count', { count: series.liveCount }));
    if (series.lastDate !== null) parts.push(untilLabel(series.lastDate));
  } else if (series.occurrenceCount !== null) {
    parts.push(t('meetings.recurrence.count', { count: series.occurrenceCount }));
  } else if (series.untilDate !== null) {
    parts.push(untilLabel(series.untilDate));
  }
  return parts.join(' · ');
}

/**
 * A törlés HATÓKÖRE szerint eltűnik-e egy sor a listából (az optimista frissítéshez — a hiteles döntés a BFF-é):
 * `this` → csak a cél · `following` → a sorozat cél utáni (és a cél) jövőbeli alkalmai · `all` → a sorozat minden
 * jövőbeli alkalma. Az elkezdődött alkalom sosem tűnik el (a BFF sem törli).
 */
export function isRemovedByScope(
  row: { id: string; startsAt: string | null; seriesId: string | null },
  target: { id: string; startsAt: string | null; seriesId: string | null },
  scope: 'this' | 'following' | 'all',
  nowMs: number,
): boolean {
  const start = row.startsAt ? Date.parse(row.startsAt) : Number.NaN;
  const notStarted = !Number.isNaN(start) && start > nowMs;
  // a cél egyedi törlésnél mindig eltűnik; sorozat-hatókörnél csak ha még nem kezdődött el (a BFF sem törli)
  if (row.id === target.id) return scope === 'this' || notStarted;
  if (scope === 'this' || row.seriesId === null || row.seriesId !== target.seriesId) return false;
  if (!notStarted) return false;
  if (scope === 'all') return true;
  const targetStart = target.startsAt ? Date.parse(target.startsAt) : Number.NaN;
  return !Number.isNaN(targetStart) && start >= targetStart;
}
